import Button from '@/components/Buttons/Button';
import { toast } from '@/components/toast';
import CopyLink from '@/images/CopyLink.svg';
import logo from '@/images/did_logo_dark.svg';
import VideoUnavailable from '@/pages/video-share/VideoNotAvailable';
import { useAnalytics } from '@/services/analytics';
import { getShareRecord } from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { copyToClipboardAsShareUrl } from '@/utils/shareUtils';
import { AppRoutes, MixPanelEvent } from '@/utils/types';
import { isMobile } from '@/utils/utils';
import { IShareRecord } from '@d-id/types';
import styled from '@emotion/styled';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const PageContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.black[50]};
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const PageHeader = styled.header`
    display: flex;
    padding: 2rem;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    max-width: 180rem;
`;

const HeaderSide = styled.div`
    display: flex;
    gap: 1rem;
`;

const Logo = styled.img`
    width: 5.6rem;
    height: 4.5rem;
`;

const PageBody = styled.main`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 110rem;
    flex: 1;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 2rem;
    flex: 1;
`;

const VideoContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 1rem;
    border: 1.4px solid ${({ theme }) => theme.colors.black[300]};
    overflow: hidden;
`;

const VideoTitle = styled.h1`
    font-size: 2.4rem;
    font-weight: 600;
`;

const VideoFooter = styled.div`
    display: flex;
    padding-top: 2.4rem;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 2.4rem;
    margin-bottom: 8.9rem;
`;

const Video = styled.video`
    width: 100%;
    height: 60vh;
    object-fit: contain;

    max-height: 60rem;
    min-height: 20rem;

    background-color: ${({ theme }) => theme.colors.black[0]};
`;

const VideoActions = styled.div`
    gap: 1rem;
`;

export const VideoShare = () => {
    const mixpanel = useAnalytics();
    const navigate = useNavigate();
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const [video, setVideo] = useState<IShareRecord | null | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);

    const { isAnonymous, loginWithRedirect } = useAuth();
    const [searchParams] = useSearchParams();

    const videoId = searchParams.get('id');
    const source = searchParams.get('utm_source');

    const handleVideoLoaded = () => {
        setIsLoading(false);
    };

    useEffect(() => {
        if (video) {
            videoRef.current?.load();
        }
    }, [video]);

    useEffect(() => {
        if (!videoId) {
            toast.error({
                toastId: 'invalid id',
                title: 'There was an error displaying the video, we are missing your video id',
            });
            navigate(AppRoutes.Home);
            return;
        }

        getShareRecord(videoId)
            .then(video => {
                setVideo(video);
                mixpanel?.track('share-page', {
                    event: MixPanelEvent.VIEW,
                    shareId: videoId,
                    videoId: video.video_id,
                    title: video.title,
                    from: source,
                    type: isMobile() ? 'mobile' : 'web',
                });
            })
            .catch(() => {
                setVideo(null);
                setIsLoading(false);
            });
    }, []);

    const VideoPlayer = useCallback(() => {
        if (!video && !isLoading) return <VideoUnavailable />;

        return (
            <>
                <Video
                    ref={videoRef}
                    onLoadedMetadata={handleVideoLoaded}
                    onPlay={() =>
                        mixpanel?.track('play-share', {
                            event: MixPanelEvent.CLICK,
                            shareId: videoId,
                            videoId: video?.video_id,
                        })
                    }
                    controls
                    poster={video?.poster}
                    loop
                    playsInline
                    crossOrigin="use-credentials">
                    <source src={video?.video} type="video/mp4" />
                    Your browser does not support the video tag.
                    <track kind="captions" src={video?.subtitles_url?.replace('.srt', '.vtt')} default />
                </Video>
            </>
        );
    }, [video, isLoading]);

    const onSignupClick = useCallback(() => {
        mixpanel?.track('signup', { event: 'click', isAnonymous, from: 'share-page' });
        loginWithRedirect({ screen_hint: 'signup' });
    }, [isAnonymous]);

    return (
        <PageContainer>
            <PageHeader>
                <HeaderSide>
                    <Link to="/" aria-label="Home">
                        <Logo src={logo} alt="logo" />
                    </Link>
                </HeaderSide>
                {/* Logged in users don't need to re-login, so we can skip rendering those */}
                {isAnonymous ? (
                    <HeaderSide>
                        <Button onClick={onSignupClick} color="secondary" variant="outlined">
                            Try D-ID for free
                        </Button>
                    </HeaderSide>
                ) : null}
            </PageHeader>
            <PageBody>
                <ContentContainer>
                    <VideoContainer>
                        <VideoPlayer aria-label="Video player" />
                    </VideoContainer>
                    <VideoFooter>
                        <VideoTitle>{video?.title}</VideoTitle>
                        <VideoActions>
                            <Button
                                variant="text"
                                color="secondary"
                                aria-label="Copy link to clipboard"
                                data-testid="share-page-copy-link-button"
                                disabled={!video}
                                startIcon={<img src={CopyLink} alt="Copy link icon" />}
                                onClick={() => {
                                    // Never reaches the return, but pleases TS
                                    if (!video) {
                                        logger.warn('Copy link clicked with no video loaded');
                                        return;
                                    }

                                    copyToClipboardAsShareUrl({ itemId: video.video_id });
                                }}>
                                Copy link
                            </Button>
                        </VideoActions>
                    </VideoFooter>
                </ContentContainer>
            </PageBody>
        </PageContainer>
    );
};
